/**
 * Read-your-writes after a successful mutation.
 *
 * The API serves reads from a replica that can lag the writer. A re-read that
 * immediately follows our own 2xx write can therefore show the PRE-write state:
 * the archive that "did not take", the rename that "reverted". Users refresh,
 * see it again, and repeat the write (prod 2026-09-03: one admin sent the same
 * archive three times in 45 seconds).
 *
 * Only the client knows it is re-reading after its own write, so it declares
 * it: every GET issued within `WINDOW_MS` of a 2xx mutation carries
 * `X-Read-Your-Writes: 1`, and vingapi routes that request's reads to the
 * writer (src/core/read_your_writes.py). The timestamp is kept in
 * sessionStorage so a full page reload inside the window still declares it —
 * "I refreshed and it was still wrong" is exactly the case this exists for.
 *
 * Wired in src/api/client.ts (response interceptor notes the write, request
 * interceptor adds the header). Nothing else needs to know.
 */

export const READ_YOUR_WRITES_HEADER = 'X-Read-Your-Writes';

/** How long after a write GETs are pinned to the writer. Aurora replica lag is
 *  normally sub-second; the window covers the spikes without making the
 *  writer serve reads for a whole session. */
export const READ_YOUR_WRITES_WINDOW_MS = 10_000;

const STORAGE_KEY = 'ving:lastWriteAt';

let lastWriteAtMs = 0;

function readStored(): number {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? Number(raw) : 0;
    return Number.isFinite(parsed) ? parsed : 0;
  } catch {
    return 0;
  }
}

/** Record that a mutation just succeeded. */
export function noteWrite(now: number = Date.now()): void {
  lastWriteAtMs = now;
  try {
    sessionStorage.setItem(STORAGE_KEY, String(now));
  } catch {
    // Storage unavailable (private mode, quota): in-memory still covers the
    // in-page re-read, which is the common case.
  }
}

/** Whether a read issued now must declare read-your-writes. */
export function shouldDeclareReadYourWrites(now: number = Date.now()): boolean {
  const last = lastWriteAt();
  return last > 0 && now - last < READ_YOUR_WRITES_WINDOW_MS;
}

/**
 * When the latest 2xx mutation was noted (`noteWrite`'s timestamp; 0 = none).
 * `useUnconfirmedSaves` (#939 I7) reads it in a write's own `onSuccess`: a
 * re-read that lands within {@link READ_YOUR_WRITES_WINDOW_MS} of it was
 * pinned to the writer and cannot be a replica lag.
 */
export function lastWriteAt(): number {
  return Math.max(lastWriteAtMs, readStored());
}

/** Test seam. */
export function resetReadYourWrites(): void {
  lastWriteAtMs = 0;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

export function isMutatingMethod(method: string | undefined): boolean {
  return MUTATING_METHODS.has((method ?? '').toUpperCase());
}
